import React, { useEffect, useId, useState } from "react";
import { SYM } from "../../utils/textSymbols";
import {
  ALPHABET_ROWS,
  DEFAULT_ROUNDS,
  GUEST_CHALK_COLORS,
  HANGMAN_PRESET_CATEGORIES,
  HANGMAN_DIFFICULTIES,
  MAX_ROUNDS,
  MIN_ROUNDS,
  categoryLabel,
  maxWrongForDifficulty
} from "./hangmanCategories.js";
import {
  createScores,
  currentPlayer,
  getMaskedChars,
  guessLetter,
  guessWord,
  isSkipped,
  sortedLeaderboard,
  startRound
} from "./hangmanEngine.js";
import { backlogStorageKey, takeWordsFromPool } from "./hangmanWordPool.js";
import "@fontsource/fredericka-the-great/400.css";
import "@fontsource/cabin-sketch/400.css";
import "@fontsource/cabin-sketch/700.css";
import "./HangmanGamePage.css";

function filterFamily(familyMembers) {
  const seen = new Set();
  return (familyMembers || []).filter((m) => {
    if (!m?.id || m.id === "all" || !m.name) return false;
    if (seen.has(m.id)) return false;
    seen.add(m.id);
    return true;
  });
}

function Gallows({ wrongCount, difficulty = "medium" }) {
  const easy = difficulty === "easy";
  // Medium/hard (9): head, body, arms, legs, nose, mouth, both X eyes
  // Easy (12): + ears, and each eye separately
  const showHead = wrongCount >= 1;
  const showBody = wrongCount >= 2;
  const showLeftArm = wrongCount >= 3;
  const showRightArm = wrongCount >= 4;
  const showLeftLeg = wrongCount >= 5;
  const showRightLeg = wrongCount >= 6;
  const showLeftEar = easy && wrongCount >= 7;
  const showRightEar = easy && wrongCount >= 8;
  const showNose = easy ? wrongCount >= 9 : wrongCount >= 7;
  const showMouth = easy ? wrongCount >= 10 : wrongCount >= 8;
  const showLeftEye = easy ? wrongCount >= 11 : wrongCount >= 9;
  const showRightEye = easy ? wrongCount >= 12 : wrongCount >= 9;

  return (
    <svg className="hm-gallows" viewBox="0 0 160 180" aria-hidden="true">
      <line className="hm-stroke" x1="20" y1="170" x2="120" y2="170" />
      <line className="hm-stroke" x1="40" y1="170" x2="40" y2="20" />
      <line className="hm-stroke" x1="40" y1="20" x2="110" y2="20" />
      <line className="hm-stroke" x1="110" y1="20" x2="110" y2="40" />
      <circle
        className={`hm-figure${showHead ? " hm-on" : ""}`}
        cx="110"
        cy="55"
        r="15"
      />
      {/* ears (easy only) */}
      <path
        className={`hm-figure${showLeftEar ? " hm-on" : ""}`}
        d="M96 50 Q90 55 96 60"
      />
      <path
        className={`hm-figure${showRightEar ? " hm-on" : ""}`}
        d="M124 50 Q130 55 124 60"
      />
      {/* nose as > shape */}
      <path
        className={`hm-figure${showNose ? " hm-on" : ""}`}
        d="M108 52 L114 55 L108 58"
      />
      {/* mouth */}
      <path
        className={`hm-figure${showMouth ? " hm-on" : ""}`}
        d="M104 62 Q110 67 116 62"
      />
      {/* X eyes — individual on easy, both together on medium/hard */}
      <g className={`hm-figure hm-eyes${showLeftEye ? " hm-on" : ""}`}>
        <line x1="102" y1="48" x2="108" y2="54" />
        <line x1="108" y1="48" x2="102" y2="54" />
      </g>
      <g className={`hm-figure hm-eyes${showRightEye ? " hm-on" : ""}`}>
        <line x1="112" y1="48" x2="118" y2="54" />
        <line x1="118" y1="48" x2="112" y2="54" />
      </g>
      <line
        className={`hm-figure${showBody ? " hm-on" : ""}`}
        x1="110"
        y1="70"
        x2="110"
        y2="115"
      />
      <line
        className={`hm-figure${showLeftArm ? " hm-on" : ""}`}
        x1="110"
        y1="85"
        x2="88"
        y2="105"
      />
      <line
        className={`hm-figure${showRightArm ? " hm-on" : ""}`}
        x1="110"
        y1="85"
        x2="132"
        y2="105"
      />
      <line
        className={`hm-figure${showLeftLeg ? " hm-on" : ""}`}
        x1="110"
        y1="115"
        x2="90"
        y2="145"
      />
      <line
        className={`hm-figure${showRightLeg ? " hm-on" : ""}`}
        x1="110"
        y1="115"
        x2="130"
        y2="145"
      />
    </svg>
  );
}

/** Happy chalk stick figure that dances when the puzzle is solved. */
function VictoryDancer({ color = "#f5e6a3" }) {
  return (
    <div className="hm-dancer-wrap" aria-hidden="true">
      <svg
        className="hm-dancer"
        viewBox="0 0 120 160"
        style={{ "--hm-dance-color": color }}
      >
        <g className="hm-dancer-body">
          <circle className="hm-dancer-stroke" cx="60" cy="32" r="16" />
          {/* happy eyes */}
          <circle className="hm-dancer-fill" cx="54" cy="30" r="2.2" />
          <circle className="hm-dancer-fill" cx="66" cy="30" r="2.2" />
          {/* smile */}
          <path className="hm-dancer-stroke" d="M52 38 Q60 46 68 38" />
          {/* torso */}
          <line className="hm-dancer-stroke" x1="60" y1="48" x2="60" y2="95" />
          {/* arms */}
          <g className="hm-dancer-arm hm-dancer-arm-l">
            <line className="hm-dancer-stroke" x1="60" y1="62" x2="38" y2="48" />
          </g>
          <g className="hm-dancer-arm hm-dancer-arm-r">
            <line className="hm-dancer-stroke" x1="60" y1="62" x2="82" y2="48" />
          </g>
          {/* legs */}
          <g className="hm-dancer-leg hm-dancer-leg-l">
            <line className="hm-dancer-stroke" x1="60" y1="95" x2="42" y2="130" />
          </g>
          <g className="hm-dancer-leg hm-dancer-leg-r">
            <line className="hm-dancer-stroke" x1="60" y1="95" x2="78" y2="130" />
          </g>
        </g>
      </svg>
      <p className="hm-dancer-caption">You got it!</p>
    </div>
  );
}

function WordDisplay({ puzzle, guessedLetters, waiting = false }) {
  if (waiting) {
    return (
      <div className="hm-word hm-word-waiting" aria-label="Preparing puzzle" aria-busy="true">
        <span className="hm-word-unit">
          {Array.from({ length: 7 }, (_, i) => (
            <span
              key={`wait-${i}`}
              className="hm-letter-slot hm-letter-slot-waiting"
              style={{ "--hm-wait-i": i }}
            >
              <span className="hm-letter-char">{"\u00A0"}</span>
              <span className="hm-letter-dash" />
            </span>
          ))}
        </span>
      </div>
    );
  }

  const chars = getMaskedChars(puzzle, guessedLetters);
  // Group letters into whole words so flex wrap never splits mid-word.
  const words = [];
  let current = [];
  chars.forEach((slot, i) => {
    if (slot.type === "space") {
      if (current.length) {
        words.push(current);
        current = [];
      }
    } else {
      current.push({ slot, i });
    }
  });
  if (current.length) words.push(current);

  return (
    <div className="hm-word" aria-label="Secret word">
      {words.map((wordSlots, wi) => (
        <span key={`w-${wi}`} className="hm-word-unit">
          {wordSlots.map(({ slot, i }) => (
            <span key={`lt-${i}`} className="hm-letter-slot">
              <span className="hm-letter-char">
                {slot.revealed ? slot.char : "\u00A0"}
              </span>
              <span className="hm-letter-dash" />
            </span>
          ))}
        </span>
      ))}
    </div>
  );
}

function ChalkButton({ selected, className = "", children, ...rest }) {
  return (
    <button
      type="button"
      className={`hm-btn${selected ? " hm-selected" : ""}${className ? ` ${className}` : ""}`}
      {...rest}
    >
      {children}
    </button>
  );
}

export default function HangmanGamePage({ apiBase, familyMembers, onBack }) {
  const guestInputId = useId();
  const family = filterFamily(familyMembers);

  const [phase, setPhase] = useState("players"); // players | difficulty | category | play | round-end | final
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [guests, setGuests] = useState([]);
  const [guestName, setGuestName] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [rounds, setRounds] = useState(DEFAULT_ROUNDS);
  const [category, setCategory] = useState("animals");
  const [customCategory, setCustomCategory] = useState("");
  const [leftPressed, setLeftPressed] = useState(false);
  const [rightPressed, setRightPressed] = useState(false);
  const [game, setGame] = useState(null);
  const [awaitingPuzzle, setAwaitingPuzzle] = useState(false);
  const [error, setError] = useState("");
  const [guessOpen, setGuessOpen] = useState(false);
  const [guessText, setGuessText] = useState("");
  const [letterMarks, setLetterMarks] = useState({}); // letter -> { color, correct }
  const [matchWords, setMatchWords] = useState([]);
  const [matchCategory, setMatchCategory] = useState("");
  const [matchCategoryLabel, setMatchCategoryLabel] = useState("");
  const [matchPlayers, setMatchPlayers] = useState([]);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [phase]);

  function toggleFamily(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function addGuest() {
    const name = guestName.trim();
    if (!name) return;
    const exists =
      guests.some((g) => g.name.toLowerCase() === name.toLowerCase()) ||
      family.some((m) => m.name.toLowerCase() === name.toLowerCase());
    if (exists) {
      setError("That name is already in the list");
      return;
    }
    setError("");
    const color = GUEST_CHALK_COLORS[guests.length % GUEST_CHALK_COLORS.length];
    const id =
      typeof crypto !== "undefined" && crypto.randomUUID
        ? `hm-guest-${crypto.randomUUID()}`
        : `hm-guest-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    const guest = {
      id,
      name,
      color
    };
    setGuests((g) => [...g, guest]);
    setSelectedIds((prev) => new Set(prev).add(guest.id));
    setGuestName("");
  }

  function removeGuest(id) {
    setGuests((g) => g.filter((x) => x.id !== id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
  }

  function buildPlayers() {
    const fromFamily = family
      .filter((m) => selectedIds.has(m.id))
      .map((m) => ({ id: m.id, name: m.name, color: m.color || "#f5e6a3" }));
    const fromGuests = guests
      .filter((g) => selectedIds.has(g.id))
      .map((g) => ({ id: g.id, name: g.name, color: g.color }));
    return [...fromFamily, ...fromGuests];
  }

  async function fetchWordBatch({ count, exclude, categoryId, signal }) {
    const res = await fetch(`${apiBase}/api/GenerateHangmanPuzzle`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        difficulty,
        category: categoryId,
        count,
        exclude,
        customCategory:
          categoryId === "custom" ? customCategory.trim() : undefined
      }),
      signal
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Could not generate words (${res.status})`);
    const words = Array.isArray(data.words)
      ? data.words.map((w) => String(w).toUpperCase()).filter(Boolean)
      : data.puzzle
        ? [String(data.puzzle).toUpperCase()]
        : [];
    if (!words.length) throw new Error("No words returned");
    return {
      words,
      category: data.category || categoryId,
      categoryLabel:
        data.categoryLabel ||
        categoryLabel(data.category || categoryId, customCategory.trim())
    };
  }

  function resolvePlayCategory() {
    if (category === "random") {
      const presets = HANGMAN_PRESET_CATEGORIES;
      return presets[Math.floor(Math.random() * presets.length)].id;
    }
    return category;
  }

  async function resolveWordBatch(count) {
    const playCategory = resolvePlayCategory();
    const storageKey = backlogStorageKey(
      playCategory,
      difficulty,
      playCategory === "custom" ? customCategory.trim() : ""
    );

    let meta = {
      category: playCategory,
      categoryLabel: categoryLabel(playCategory, customCategory.trim())
    };

    const words = await takeWordsFromPool({
      need: count,
      storageKey,
      fetchMore: async ({ count: fetchCount, exclude }) => {
        const batch = await fetchWordBatch({
          count: fetchCount,
          exclude,
          categoryId: playCategory
        });
        meta = {
          category: batch.category || playCategory,
          categoryLabel: batch.categoryLabel || meta.categoryLabel
        };
        return batch.words;
      }
    });

    return {
      words,
      category: meta.category,
      categoryLabel: meta.categoryLabel
    };
  }

  function startRoundFromMatch({
    players,
    scores,
    words,
    categoryId,
    categoryTitle,
    round,
    turnIndex
  }) {
    const puzzle = words[round - 1];
    if (!puzzle) throw new Error("Missing word for this round");
    const next = startRound({
      puzzle,
      players,
      scores,
      round,
      totalRounds: words.length,
      turnIndex,
      difficulty,
      maxWrong: maxWrongForDifficulty(difficulty)
    });
    setMatchWords(words);
    setMatchCategory(categoryId);
    setMatchCategoryLabel(categoryTitle || categoryLabel(categoryId, customCategory.trim()));
    setMatchPlayers(players);
    setLetterMarks({});
    setAwaitingPuzzle(false);
    setGame(next);
    setPhase("play");
  }

  async function beginMatch() {
    const players = buildPlayers();
    if (!players.length) {
      setError("Pick at least one player");
      return;
    }
    if (category === "custom" && !customCategory.trim()) {
      setError("Type a custom category or pick another one");
      return;
    }

    const categoryTitle = categoryLabel(category, customCategory.trim());
    setError("");
    setGuessOpen(false);
    setGuessText("");
    setLetterMarks({});
    setMatchWords([]);
    setMatchCategory(category);
    setMatchCategoryLabel(categoryTitle);
    setMatchPlayers(players);
    setAwaitingPuzzle(true);
    setGame({
      puzzle: "",
      players,
      scores: createScores(players),
      turnIndex: 0,
      guessedLetters: [],
      wrongCount: 0,
      maxWrong: maxWrongForDifficulty(difficulty),
      skippedIds: [],
      status: "playing",
      round: 1,
      totalRounds: rounds,
      lastAction: null
    });
    setPhase("play");

    try {
      const batch = await resolveWordBatch(rounds);
      startRoundFromMatch({
        players,
        scores: null,
        words: batch.words,
        categoryId: batch.category,
        categoryTitle: batch.categoryLabel,
        round: 1,
        turnIndex: 0
      });
    } catch (err) {
      setAwaitingPuzzle(false);
      setGame(null);
      setMatchPlayers([]);
      setMatchWords([]);
      setError(err.message || "Failed to start game");
      setPhase("category");
    }
  }

  function onLetter(letter) {
    if (!game || awaitingPuzzle || game.status !== "playing") return;
    if (game.guessedLetters.includes(letter)) return;

    const guesser = currentPlayer(game);
    const next = guessLetter(game, letter);
    const wasCorrect = (next.lastAction?.type || "").includes("correct");
    setLetterMarks((prev) => ({
      ...prev,
      [letter]: {
        color: guesser.color || "#f5e6a3",
        correct: wasCorrect,
        playerId: guesser.id
      }
    }));
    setGame(next);

    if (next.status === "won") {
      window.setTimeout(() => setPhase("round-end"), 2400);
    } else if (next.status === "lost") {
      window.setTimeout(() => setPhase("round-end"), 600);
    }
  }

  function submitWordGuess() {
    if (!game || awaitingPuzzle || game.status !== "playing") return;
    const next = guessWord(game, guessText);
    if (next === game && !guessText.trim()) return;
    setGame(next);
    setGuessOpen(false);
    setGuessText("");
    if (next.status === "won") {
      window.setTimeout(() => setPhase("round-end"), 2400);
    } else if (next.status === "lost") {
      window.setTimeout(() => setPhase("round-end"), 600);
    }
  }

  function continueAfterRound() {
    if (!game) return;
    if (game.round >= game.totalRounds) {
      setPhase("final");
      return;
    }
    const nextTurn =
      game.status === "won"
        ? (game.turnIndex + 1) % game.players.length
        : game.turnIndex;
    try {
      startRoundFromMatch({
        players: matchPlayers.length ? matchPlayers : game.players,
        scores: game.scores,
        words: matchWords,
        categoryId: matchCategory,
        categoryTitle: matchCategoryLabel,
        round: game.round + 1,
        turnIndex: nextTurn
      });
    } catch (err) {
      setError(err.message || "Could not start next round");
    }
  }

  function playAgain() {
    setGame(null);
    setAwaitingPuzzle(false);
    setMatchWords([]);
    setMatchCategory("");
    setMatchCategoryLabel("");
    setMatchPlayers([]);
    setLetterMarks({});
    setSelectedIds(new Set());
    setCustomCategory("");
    setError("");
    setPhase("players");
  }

  const boardLocked = awaitingPuzzle || !game || game.status !== "playing";

  const playerCount = buildPlayers().length;
  const turn = game ? currentPlayer(game) : null;
  const displayCategory =
    matchCategoryLabel ||
    categoryLabel(matchCategory || category, customCategory.trim());

  return (
    <div className="hm-page">
      <header className="hm-header">
        <button
          type="button"
          className="hm-back-btn"
          onClick={() => {
            if (phase === "players" || phase === "final") onBack?.();
            else if (phase === "difficulty") setPhase("players");
            else if (phase === "category") setPhase("difficulty");
            else if (awaitingPuzzle) return;
            else onBack?.();
          }}
          aria-label={phase === "players" || phase === "final" || phase === "play" || phase === "round-end" ? "Back to Games" : "Back"}
        >
          {SYM.arrowLeft}
        </button>
        <h1 className="hm-title">HANGMAN</h1>
        <span className="hm-title-spacer" aria-hidden="true" />
      </header>

      {error ? <p className="hm-error" role="alert">{error}</p> : null}

      {phase === "players" && (
        <div className="hm-wizard">
          <p className="hm-step-label">Step 1 · Who&apos;s playing?</p>
          <p className="hm-diff-note">Tap names to select them. Only selected players will take turns.</p>
          <p className="hm-section-title">Family</p>
          {family.length === 0 ? (
            <p className="hm-empty">No family members loaded. Add guests below.</p>
          ) : (
            <div className="hm-player-grid">
              {family.map((m) => {
                const selected = selectedIds.has(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    className={`hm-chip${selected ? " hm-accent-selected" : ""}`}
                    style={
                      selected
                        ? { "--hm-chip-accent": m.color || "#f5e6a3" }
                        : undefined
                    }
                    onClick={() => toggleFamily(m.id)}
                  >
                    {m.name}
                  </button>
                );
              })}
            </div>
          )}

          <p className="hm-section-title">Guests</p>
          {guests.length > 0 && (
            <div className="hm-player-grid">
              {guests.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  className={`hm-chip${selectedIds.has(g.id) ? " hm-accent-selected" : ""}`}
                  style={
                    selectedIds.has(g.id)
                      ? { "--hm-chip-accent": g.color }
                      : undefined
                  }
                  onClick={() => removeGuest(g.id)}
                  title="Tap to remove guest"
                >
                  {g.name} {SYM.close}
                </button>
              ))}
            </div>
          )}
          <div className="hm-guest-row">
            <label className="hm-visually-hidden" htmlFor={guestInputId}>
              Guest name
            </label>
            <input
              id={guestInputId}
              className="hm-guest-input"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addGuest();
                }
              }}
              placeholder="Add guest name"
              maxLength={40}
            />
            <ChalkButton onClick={addGuest} disabled={!guestName.trim()}>
              Add
            </ChalkButton>
          </div>

          <div className="hm-btn-row">
            <ChalkButton
              className="hm-btn-accent"
              disabled={playerCount < 1}
              onClick={() => {
                setError("");
                setPhase("difficulty");
              }}
            >
              Next
            </ChalkButton>
          </div>
        </div>
      )}

      {phase === "difficulty" && (
        <div className="hm-wizard">
          <p className="hm-step-label">Step 2 · Difficulty &amp; rounds</p>
          <p className="hm-diff-note">
            Easy: 12 misses (ears + each eye). Medium &amp; Hard: 9 misses. Hard uses tougher words.
          </p>
          <div className="hm-choice-grid">
            {HANGMAN_DIFFICULTIES.map((d) => (
              <button
                key={d.id}
                type="button"
                className={`hm-choice${difficulty === d.id ? " hm-selected" : ""}`}
                onClick={() => setDifficulty(d.id)}
              >
                {d.label}
              </button>
            ))}
          </div>

          <div className="hm-rounds" aria-label="Number of rounds">
            <span className="hm-rounds-label">Rounds:</span>
            <button
              type="button"
              className={`hm-rounds-arrow${leftPressed ? " hm-pressed" : ""}`}
              disabled={rounds <= MIN_ROUNDS}
              onPointerDown={() => setLeftPressed(true)}
              onPointerUp={() => setLeftPressed(false)}
              onPointerLeave={() => setLeftPressed(false)}
              onClick={() => setRounds((n) => Math.max(MIN_ROUNDS, n - 1))}
              aria-label="Fewer rounds"
            >
              {SYM.chevronLeft}
            </button>
            <span className="hm-rounds-value">{rounds}</span>
            <button
              type="button"
              className={`hm-rounds-arrow${rightPressed ? " hm-pressed" : ""}`}
              disabled={rounds >= MAX_ROUNDS}
              onPointerDown={() => setRightPressed(true)}
              onPointerUp={() => setRightPressed(false)}
              onPointerLeave={() => setRightPressed(false)}
              onClick={() => setRounds((n) => Math.min(MAX_ROUNDS, n + 1))}
              aria-label="More rounds"
            >
              {SYM.chevronRight}
            </button>
          </div>

          <div className="hm-btn-row">
            <ChalkButton onClick={() => setPhase("players")}>Back</ChalkButton>
            <ChalkButton className="hm-btn-accent" onClick={() => setPhase("category")}>
              Next
            </ChalkButton>
          </div>
        </div>
      )}

      {phase === "category" && (
        <div className="hm-wizard">
          <p className="hm-step-label">Step 3 · Category</p>
          <div className="hm-choice-grid">
            {HANGMAN_PRESET_CATEGORIES.map((c) => (
              <button
                key={c.id}
                type="button"
                className={`hm-choice${category === c.id ? " hm-selected" : ""}${c.id === "potty" ? " hm-potty" : ""}`}
                onClick={() => setCategory(c.id)}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="hm-category-extra">
            <button
              type="button"
              className={`hm-choice hm-choice-random${category === "random" ? " hm-selected" : ""}`}
              onClick={() => setCategory("random")}
            >
              Random
            </button>
            <input
              className={`hm-guest-input hm-custom-category${category === "custom" ? " hm-custom-selected" : ""}`}
              value={customCategory}
              onChange={(e) => {
                setCustomCategory(e.target.value);
                setCategory("custom");
              }}
              onFocus={() => setCategory("custom")}
              placeholder="Custom category"
              maxLength={40}
              aria-label="Custom category"
            />
          </div>
          {category === "custom" && !customCategory.trim() ? (
            <p className="hm-diff-note">Type a theme for Grok (e.g. dinosaurs, camping).</p>
          ) : null}
          <div className="hm-btn-row">
            <ChalkButton onClick={() => setPhase("difficulty")}>Back</ChalkButton>
            <ChalkButton
              className="hm-btn-accent"
              disabled={category === "custom" && !customCategory.trim()}
              onClick={beginMatch}
            >
              Start game
            </ChalkButton>
          </div>
        </div>
      )}

      {phase === "play" && game && (
        <div className={`hm-play${awaitingPuzzle ? " hm-play-waiting" : ""}`}>
          <WordDisplay
            puzzle={game.puzzle}
            guessedLetters={game.guessedLetters}
            waiting={awaitingPuzzle}
          />

          <div className="hm-meta">
            <span>
              Round {game.round} / {game.totalRounds}
            </span>
            <span>
              Misses {game.wrongCount}/{game.maxWrong || maxWrongForDifficulty(difficulty)}
            </span>
          </div>
          <div className="hm-scores">
            {game.players.map((p) => {
              const skipped = isSkipped(game, p.id);
              return (
                <span
                  key={p.id}
                  className={`hm-score-item${turn?.id === p.id ? " hm-active" : ""}${skipped ? " hm-skipped" : ""}`}
                  style={
                    turn?.id === p.id
                      ? { "--hm-score-color": p.color || "#f5e6a3" }
                      : undefined
                  }
                >
                  {p.name}: <span>{game.scores[p.id] || 0}</span>
                  {skipped ? " (skipped)" : ""}
                </span>
              );
            })}
          </div>

          <div className="hm-stage">
            <div className={`hm-gallows-wrap${awaitingPuzzle ? " hm-gallows-drawing" : ""}`}>
              {game.status === "won" ? (
                <VictoryDancer color={turn?.color || "#f5e6a3"} />
              ) : (
                <Gallows wrongCount={game.wrongCount} difficulty={difficulty} />
              )}
            </div>
            <aside className="hm-side-panel">
              <h2 className="hm-category-heading">{displayCategory}</h2>
              {turn && (
                <div
                  className="hm-turn-card"
                  aria-live="polite"
                  style={{ "--hm-turn-color": turn.color || "#f5e6a3" }}
                >
                  <span className="hm-turn-label">
                    {awaitingPuzzle ? "Getting ready" : "Whose turn"}
                  </span>
                  <span className="hm-turn-name">{turn.name}</span>
                  <ChalkButton
                    className="hm-btn-accent hm-turn-solve"
                    disabled={boardLocked}
                    onClick={() => {
                      setGuessText("");
                      setGuessOpen(true);
                    }}
                  >
                    Solve
                  </ChalkButton>
                </div>
              )}
            </aside>
          </div>

          <div className="hm-alphabet">
            {ALPHABET_ROWS.map((row) => (
              <div key={row.join("")} className="hm-alpha-row">
                {row.map((letter) => {
                  const used = game.guessedLetters.includes(letter);
                  const mark = letterMarks[letter];
                  return (
                    <button
                      key={letter}
                      type="button"
                      className={`hm-letter-btn${used ? " hm-used" : ""}${mark?.correct ? " hm-used-correct" : ""}`}
                      style={
                        mark?.color
                          ? { "--hm-strike-color": mark.color }
                          : undefined
                      }
                      disabled={used || boardLocked}
                      onClick={() => onLetter(letter)}
                      aria-label={`Guess ${letter}`}
                    >
                      {letter}
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          {game.lastAction?.type === "word-wrong-skip" ? (
            <p className="hm-solve-penalty" role="status">
              Wrong solve — points wiped and skipped for this round
            </p>
          ) : null}
        </div>
      )}

      {phase === "round-end" && game && (
        <div className="hm-wizard">
          {game.status === "won" ? (
            <div className="hm-round-win">
              <VictoryDancer color={currentPlayer(game)?.color || "#f5e6a3"} />
              <p className="hm-round-banner">Solved!</p>
            </div>
          ) : (
            <p className="hm-round-banner">Out of chalk misses</p>
          )}
          <p className="hm-reveal">
            The answer was <strong>{game.puzzle}</strong>
          </p>
          <ul className="hm-leaderboard">
            {sortedLeaderboard(game).map((p) => (
              <li key={p.id}>
                <span>{p.name}</span>
                <span>{p.score}</span>
              </li>
            ))}
          </ul>
          <div className="hm-btn-row">
            {game.round >= game.totalRounds ? (
              <ChalkButton className="hm-btn-accent" onClick={() => setPhase("final")}>
                Final scores
              </ChalkButton>
            ) : (
              <ChalkButton className="hm-btn-accent" onClick={continueAfterRound}>
                Next round
              </ChalkButton>
            )}
          </div>
        </div>
      )}

      {phase === "final" && game && (
        <div className="hm-wizard">
          <p className="hm-step-label">Final scoreboard</p>
          <ul className="hm-leaderboard">
            {sortedLeaderboard(game).map((p, i) => (
              <li key={p.id}>
                <span>
                  {i + 1}. {p.name}
                </span>
                <span>{p.score}</span>
              </li>
            ))}
          </ul>
          <div className="hm-btn-row">
            <ChalkButton onClick={onBack}>Games</ChalkButton>
            <ChalkButton className="hm-btn-accent" onClick={playAgain}>
              Play again
            </ChalkButton>
          </div>
        </div>
      )}

      {guessOpen && !awaitingPuzzle && (
        <div className="hm-modal-backdrop" role="presentation" onClick={() => setGuessOpen(false)}>
          <div
            className="hm-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Solve the word"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Solve</h2>
            <p style={{ marginTop: 0, color: "var(--hm-chalk-dim)" }}>
              {turn
                ? `${turn.name}'s turn — type the full answer`
                : "Type the full word or phrase"}
            </p>
            <p style={{ color: "var(--hm-chalk-pink)", fontSize: "0.95rem" }}>
              Wrong answer: lose all your points and get skipped this round
            </p>
            <input
              className="hm-modal-input"
              value={guessText}
              onChange={(e) => setGuessText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submitWordGuess();
                }
              }}
              placeholder="Type the full word or phrase"
              autoFocus
              autoComplete="off"
              spellCheck={false}
            />
            <div className="hm-modal-actions">
              <ChalkButton onClick={() => setGuessOpen(false)}>Cancel</ChalkButton>
              <ChalkButton
                className="hm-btn-accent"
                disabled={!guessText.trim()}
                onClick={submitWordGuess}
              >
                Solve
              </ChalkButton>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
